import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { useUser } from "@/apis/bff"
import { Banner } from "@/components/ui/banner"
import { CardBody } from "@/components/ui/card"
import { EditorView } from "@/components/ui/editor"
import { useCurrentLanguage } from "@/hooks/useCurrentLanguage"
import { joinStrings } from "@/lib/joinString"
import { cn } from "@/lib/utils"
import { useCareProviderWithOrigin } from "@/modules/care-provider/services/useCareProviderWithOrigin"
import { DoctorInfo } from "@/modules/patients/components/doctor-info"
import { DeBetterDocRecommendedCareProviders } from "@/modules/patients/detail/better-doc/de/components/de-better-doc-recommended-care-providers"
import { hasDeBetterDocRecommendedCareProviders } from "@/modules/patients/detail/better-doc/de/lib/has-de-better-doc-recommended-care-providers"
import { DocumentViewerDialog } from "@/modules/patients/detail/conclude/document-viewer-dialog"
import { useEditMode } from "@/modules/patients/detail/hooks/useEditMode"
import { AttachmentsSection } from "@/modules/patients/detail/progress-note/attachments"
import { EditCareProviderDialog } from "@/modules/patients/detail/progress-note/components/edit-care-provider-dialog"
import { GeneralHealthInformationSummary } from "@/modules/patients/detail/progress-note/components/general-health-information-summary"
import { TreatmentPlanReadonlySection } from "@/modules/patients/detail/progress-note/components/progress-note-editable/treatment-plan-section"
import { FollowUpAppointmentReadOnlySection } from "@/modules/patients/detail/progress-note/components/progress-note-follow-up"
import { ProgressNoteReadOnlyCardSection } from "@/modules/patients/detail/progress-note/components/progress-note-readonly-card-section"
import { QuestionnaireReadOnly } from "@/modules/patients/detail/progress-note/components/questionnaire-readonly"
import { RequestType } from "@/modules/patients/detail/progress-note/components/request-type"
import { RequestedMedicationsSummary } from "@/modules/patients/detail/progress-note/components/requested-medications-summary"
import { VitalSignsSummary } from "@/modules/patients/detail/progress-note/components/vital-signs-summary"
import { Doc2UAttachments } from "@/modules/patients/detail/progress-note/doc2u/doc2u-attachments"
import { DocumentListReadOnlySection } from "@/modules/patients/detail/progress-note/documents/document-list-read-only-section"
import { useAppointmentAttachments } from "@/modules/patients/detail/progress-note/hooks/useAppointmentAttachments"
import { mapPointOfCareSpecialty } from "@/modules/patients/detail/progress-note/lib/map-point-of-care-specialty"
import {
  getPurposeItems,
  pointOfCareItems,
  reasonForChoosingItems,
  timeToTreatItems,
} from "@/modules/patients/detail/progress-note/point-of-care-items"
import {
  ProgressNoteData,
  ProgressNotePointOfCare,
} from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { RecommendedMedicationsReadonlySection } from "@/modules/patients/detail/recommended-medications/recommended-medication-readonly-section"
import {
  formatProgressNoteDocuments,
  SelectedDocumentForViewingType,
  useDocumentStore,
} from "@/modules/patients/detail/store/useDocumentStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { isConcludedCarePlanDocument } from "@/modules/patients/libs/is-concluded-care-plan-document"
import { QuestionnaireCriticalitySummaryFragment$key } from "@/modules/patients/services/__generated__/QuestionnaireCriticalitySummaryFragment.graphql"
import { QuestionnaireCriticalitySummaryFragment } from "@/modules/patients/services/QuestionnaireCriticalitySummaryFragment"
import { AppointmentAttachmentFragment$key } from "@/modules/waiting-room/services/__generated__/AppointmentAttachmentFragment.graphql"
import { PointOfCareType } from "@/types/__generated__/union-types"

interface ProgressNoteReadOnlyCardBodyProps {
  progressNote: ProgressNoteData
}

const ProgressNoteReadOnlyCardBody = ({ progressNote }: ProgressNoteReadOnlyCardBodyProps) => {
  const { t } = useTranslation()
  const {
    appointment: {
      reason,
      appointmentAttachments,
      id,
      telephoneLine,
      createdAt: appointmentCreatedAt,
      questionnaireId,
      questionnaireCriticalitySummary,
      requestType,
    },
    pointOfCare,
    treatmentPlan,
    anamnesis,
    followUpAppointment,
  } = progressNote

  const criticalitySummary = useFragment<QuestionnaireCriticalitySummaryFragment$key>(
    QuestionnaireCriticalitySummaryFragment,
    questionnaireCriticalitySummary,
  )

  return (
    <CardBody>
      {requestType !== "NONE" && <RequestType requestType={requestType} data-testid="request-type-select" />}
      <ProgressNoteReadOnlyCardSection
        title={t("episodeOfCare.progressNote.reasonForEncounter")}
        testId="reason-for-encounter-read-only">
        <div className="grid gap-4">
          <span>{reason}</span>
          {progressNote.appointment.healthOverview?.vitalSign && (
            <VitalSignsSummary vitalSignKey={progressNote.appointment.healthOverview.vitalSign} />
          )}
          {progressNote.appointment.healthOverview?.generalHealthInformation && (
            <GeneralHealthInformationSummary
              generalHealthInformationKey={progressNote.appointment.healthOverview.generalHealthInformation}
            />
          )}
          <RequestedMedicationsSummary appointmentRef={progressNote.appointment} />
          {questionnaireId && criticalitySummary && (
            <QuestionnaireReadOnly
              completedAt={appointmentCreatedAt}
              name={criticalitySummary.useCaseName}
              questionnaireId={questionnaireId}
              questionnaireCriticalitySummary={questionnaireCriticalitySummary}
              appointmentId={progressNote.appointment.id}
              asyncRequestStatus={progressNote.appointment.asyncRequestStatus}
              channel={progressNote.appointment.channel}
            />
          )}
          <AttachmentsSection
            appointmentAttachments={appointmentAttachments}
            telephoneLine={telephoneLine}
            appointmentId={id}
            isReadOnlyProgressNote
          />
        </div>
      </ProgressNoteReadOnlyCardSection>
      <AnamnesisSection anamnesis={anamnesis} appointmentAttachments={appointmentAttachments} />
      {pointOfCare && <PointOfCareSection pointOfCare={pointOfCare} progressNote={progressNote} />}
      {treatmentPlan && <TreatmentPlanReadonlySection treatmentPlan={treatmentPlan} />}
      {followUpAppointment && <FollowUpAppointmentReadOnlySection followUpAppointment={followUpAppointment} />}
      <RecommendedMedicationsReadonlySection progressNote={progressNote} />
      <DocumentsReadOnlySection progressNote={progressNote} />
    </CardBody>
  )
}

interface PointOfCareProps {
  pointOfCare?: ProgressNotePointOfCare
  progressNote: ProgressNoteData
}

const PointOfCareSection = ({ ...props }: PointOfCareProps) => {
  const { pointOfCare, progressNote } = props
  const { isOneOf } = useUser()
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const isEditMode = useEditMode()
  const {
    medical: medicalPointOfCare,
    actual: actualPointOfCare,
    reasonForChoosingActual,
    timeToTreatType,
    purpose,
  } = pointOfCare ?? {
    medical: null,
    actual: null,
    reasonForChoosingActual: null,
    specialty: null,
    timeToTreatType: null,
    purpose: null,
  }
  const careProviderId = (pointOfCare?.__typename === "ch_PointOfCare" ? pointOfCare.careProvider?.id : "") ?? ""
  const isBetterDocUsed = !!progressNote.betterDoc?.isUsed
  const deBetterDocResult = progressNote.__typename === "de_ProgressNote" ? progressNote.betterDocResult : null

  const careProvider = useCareProviderWithOrigin(careProviderId, !careProviderId)
  const country = useCountryParam()
  const { t } = useTranslation()
  const language = useCurrentLanguage()

  const pocValue: PointOfCareType | null = actualPointOfCare ?? medicalPointOfCare
  const careProviderSpecialty = pointOfCare?.__typename === "ch_PointOfCare" ? pointOfCare.careProviderSpecialty : null
  const activePointOfCare: string | undefined = actualPointOfCare
    ? pointOfCareItems.find((a) => a.value === actualPointOfCare)?.label
    : pointOfCareItems.find((a) => a.value === medicalPointOfCare)?.label

  const onlyPoc: PointOfCareType[] = ["TELECARE", "INCOMPLETE_TELECONSULTATION", "EMERGENCY_CALL"]
  const showTreatmentTIme: boolean = !!pocValue && !onlyPoc.includes(pocValue)
  const showReasons: boolean = pocValue === "GENERAL_PRACTITIONER"
  const showSpecialty: boolean = !!pocValue && ["SPECIALIST", "HOSPITAL_ELECTIVE"].includes(pocValue)
  const showCareProvider: boolean = country === "CH"

  const treatmentTime = timeToTreatType && timeToTreatItems.find((a) => a.value === timeToTreatType)?.label
  const purposeItems: string[] = getPurposeItems(purpose)
  const subText: string = joinStrings([
    showTreatmentTIme && treatmentTime ? treatmentTime : "",
    showReasons && !!purposeItems.length ? purposeItems.join(", ") : "",
  ])

  const cleanedCareProviderSpecialty =
    careProviderSpecialty?.specialtyTranslations
      .filter((translation) => translation.language === language.toUpperCase())
      .map((translation) => ({
        id: careProviderSpecialty.id,
        language: translation.language,
        word: translation.name,
        synonym: "",
      })) || []

  let selectedSpecialty: { word: string } | null | undefined
  if (pointOfCare?.__typename === "ch_PointOfCare") {
    selectedSpecialty = pointOfCare.pointOfCareSpecialty
      ? mapPointOfCareSpecialty(pointOfCare.pointOfCareSpecialty, language)
      : cleanedCareProviderSpecialty[0]
  } else if (pointOfCare?.__typename === "de_PointOfCare") {
    selectedSpecialty = pointOfCare.pointOfCareSpecialty
      ? mapPointOfCareSpecialty(pointOfCare.pointOfCareSpecialty, language)
      : null
  }

  const canChangeCareProvider =
    isEditMode &&
    country === "CH" &&
    !!careProvider &&
    !!medicalPointOfCare &&
    !isBetterDocUsed &&
    progressNote.appointment.isCompleted &&
    isOneOf(["MedicalSupervisor", "CustomerServiceCenter"])

  return (
    <ProgressNoteReadOnlyCardSection
      className="border-t"
      title={t("episodeOfCare.progressNote.pointOfCare.title")}
      testId="point-of-care-read-only">
      <div className="flex flex-col items-start gap-4">
        <div className="flex flex-col">
          <div className={cn("text-on-surface flex items-center gap-2")}>
            <span>{activePointOfCare}</span>
            {showSpecialty && selectedSpecialty && <span>{selectedSpecialty.word}</span>}
          </div>
          <div className="text-on-surface-subdued flex gap-1 text-xs leading-4 tracking-[0.25px]">{subText}</div>
        </div>
        {showCareProvider && careProvider && (
          <DoctorInfo
            readOnly
            role="SPECIALIST"
            canHover={false}
            id="poc-care-provider"
            value={careProvider}
            onEdit={canChangeCareProvider ? () => setIsEditDialogOpen(true) : undefined}
          />
        )}
        {deBetterDocResult && hasDeBetterDocRecommendedCareProviders(progressNote) ? (
          <DeBetterDocRecommendedCareProviders recommendedCareProviders={deBetterDocResult.recommendedCareProviders} />
        ) : null}
        {canChangeCareProvider && isEditDialogOpen && (
          <EditCareProviderDialog
            open={isEditDialogOpen}
            onOpenChange={setIsEditDialogOpen}
            progressNote={progressNote}
            careProviderId={careProviderId}
          />
        )}

        {medicalPointOfCare && actualPointOfCare && (
          <Banner type="inline" variant="info" className="py-0 pr-0" iconClassName="py-2">
            <p className="mt-0.5 flex-1 space-x-1 py-2">
              <span>
                {t("episodeOfCare.progressNote.pointOfCare.medicalPointOfCareInfo", {
                  medicalPointOfCare: pointOfCareItems.find((a) => a.value === medicalPointOfCare)?.label,
                })}
              </span>
              {reasonForChoosingActual && (
                <span>
                  {t("episodeOfCare.progressNote.pointOfCare.reasonForChoosing", {
                    reasonForChoosingActual: reasonForChoosingItems.find((a) => a.value === reasonForChoosingActual)
                      ?.label,
                  })}
                </span>
              )}
            </p>
          </Banner>
        )}
      </div>
    </ProgressNoteReadOnlyCardSection>
  )
}

const isAnamnesisEmpty = (anamnesis: string | null) => {
  return !anamnesis || anamnesis.trim().length === 0
}
const AnamnesisSection = ({
  anamnesis,
  appointmentAttachments,
}: {
  anamnesis: string | null
  appointmentAttachments: AppointmentAttachmentFragment$key
}) => {
  const { t } = useTranslation()
  const doc2UAttachments = useAppointmentAttachments({ uploadSources: ["DOC2_U"], appointmentAttachments })

  if (isAnamnesisEmpty(anamnesis) && doc2UAttachments.length === 0) return null

  return (
    <ProgressNoteReadOnlyCardSection
      className="border-t"
      title={t("episodeOfCare.progressNote.anamnesis.title")}
      testId="anamnesis-read-only">
      <div className="grid gap-4">
        {anamnesis && <EditorView name="anamnesis" initialValue={anamnesis.trim()} />}
        <Doc2UAttachments appointmentAttachments={appointmentAttachments} />
      </div>
    </ProgressNoteReadOnlyCardSection>
  )
}

const DocumentsReadOnlySection = ({ progressNote }: ProgressNoteReadOnlyCardBodyProps) => {
  const { t } = useTranslation()
  const [isOpenDialog, setIsOpenDialog] = useState(false)
  const appointmentId = useAppointmentParam()
  const isEditMode = useEditMode()
  const formattedProgressNoteData = formatProgressNoteDocuments(progressNote, false)
  const { carePlanSummary, ...documentListWithoutCarePlanSummary } = formattedProgressNoteData
  const hasDocument =
    Object.values(documentListWithoutCarePlanSummary ?? {}).some((value) => value) ||
    isConcludedCarePlanDocument(carePlanSummary)

  const setSelectedDocumentForViewing = useDocumentStore((state) => state.actions.setSelectedDocumentForViewing)
  const selectedDocumentForViewing = useDocumentStore((state) => state.selectedDocumentForViewing)

  const handleViewDocument = ({ type, fileId, signedAt, signedBy, sentAt, sentBy }: SelectedDocumentForViewingType) => {
    const document =
      type === "referralLetter"
        ? { type, fileId, signedAt, signedBy, sentAt, sentBy }
        : { type, fileId, signedAt, signedBy }

    setIsOpenDialog(true)
    setSelectedDocumentForViewing(document)
  }

  if (hasDocument || (!hasDocument && !appointmentId && isEditMode)) {
    return (
      <ProgressNoteReadOnlyCardSection
        className="border-t"
        title={t("episodeOfCare.progressNote.documents")}
        testId="progress-note-documents-read-only">
        <DocumentListReadOnlySection progressNote={progressNote} onSelect={handleViewDocument} />
        {isOpenDialog && selectedDocumentForViewing?.type && (
          <DocumentViewerDialog type={selectedDocumentForViewing.type} onOpenChange={setIsOpenDialog} />
        )}
      </ProgressNoteReadOnlyCardSection>
    )
  }

  return null
}

export { isAnamnesisEmpty, ProgressNoteReadOnlyCardBody }
